import styled from "styled-components/macro";
import { TextField } from "../shared/TextField/TextField";
import { Password } from "../shared/TextField/Password";
import { DeviceType } from "../../utils/hooks/useDeviceHook";

const formFieldWidth = (deviceType: string) => {
  if (deviceType === "desktop") return "453px";
  if (deviceType === "tablet") return "421px";
  return "327px";
};

export const CenterContainter = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  place-items: center;
  margin: 0 auto;
  grid-template-rows: min-content min-content min-content;
  width: 100%;
`;

export const DesktopFormCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
`;

export const LoginFormColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  max-width: 520px;
`;

export const LoginHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  text-align: center;
  width: 100%;
  max-width: 453px;
`;

export const LoginFormWrapper = styled.div`
  display: grid;
  width: 100%;
`;

export const LoginFieldsSection = styled.div<{ deviceType: DeviceType }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding-top: 24px;
  width: 100%;
`;

export const LoginError = styled.div`
  margin: 20px;
  max-width: 75%;
`;

export const LoginFormFieldInput = styled.div<{
  deviceType: DeviceType;
}>`
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
  justify-self: center;
`;

export const LoginInput = styled(TextField)`
  width: 100%;
  && {
    .MuiOutlinedInput-root {
      border-radius: 4px;
      background-color: ${({ theme }) => theme.white};
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.greyLight};
    }
    .MuiOutlinedInput-input {
      padding: 16px 12px;
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.greyMedium};
    }
    .MuiInputLabel-root {
      color: ${({ theme }) => theme.greyDark};
      font-size: 12px;
    }
    .MuiInputLabel-asterisk {
      color: ${({ theme }) => theme.redDark};
    }
  }
`;

export const LoginPassword = styled(Password)`
  width: 100%;
  && {
    .MuiOutlinedInput-root {
      border-radius: 4px;
      background-color: ${({ theme }) => theme.white};
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.greyLight};
    }
    .MuiOutlinedInput-input {
      padding: 16px 12px;
      font-size: 16px;
      line-height: 24px;
      color: ${({ theme }) => theme.greyMedium};
    }
    .MuiInputLabel-root {
      color: ${({ theme }) => theme.greyDark};
      font-size: 12px;
    }
    .MuiInputLabel-asterisk {
      color: ${({ theme }) => theme.redDark};
    }
    .MuiIconButton-root {
      color: ${({ theme }) => theme.greyLight};
    }
  }
`;

export const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  place-items: center;
  margin-top: 12px;
`;

export const PasswordLinkContainer = styled.div<{
  deviceType: DeviceType;
}>`
  display: flex;
  justify-content: flex-end;
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
  text-align: end;

  a {
    text-decoration: none;
  }
`;

export const DesktopContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background-color: white;
`;

export const NonDesktopShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  background-color: ${({ theme }) => theme.white};
`;

export const LoginHeroBand = styled.div<{ $deviceType: DeviceType }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding-left: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "16px" : "24px"};
  padding-right: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "16px" : "24px"};
  padding-bottom: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "32px" : "40px"};
  padding-top: ${({ $deviceType }) =>
    $deviceType === "mobile"
      ? "calc(32px + env(safe-area-inset-top, 0px))"
      : "calc(40px + env(safe-area-inset-top, 0px))"};
  background: ${({ theme, $deviceType }) =>
    $deviceType === "mobile"
      ? theme.loginHeroGradientMobile
      : theme.loginHeroGradient};
`;

export const NonDesktopContents = styled.div<{ $deviceType: DeviceType }>`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  padding-top: ${({ $deviceType }) =>
    $deviceType === "mobile" ? "32px" : "80px"};
  padding-left: ${({ $deviceType }) =>
    $deviceType === "tablet" ? "clamp(24px, 12vw, 96px)" : "16px"};
  padding-right: ${({ $deviceType }) =>
    $deviceType === "tablet" ? "clamp(24px, 12vw, 96px)" : "16px"};
  padding-bottom: calc(48px + env(safe-area-inset-bottom, 0px));
`;

export const MobileContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
`;

export const DesktopColorBackground = styled.div`
  position: relative;
  min-height: 100vh;
  height: 100%;
  overflow: hidden;
  background-color: ${({ theme }) => theme.loginBrandPanel};
`;

export const DesktopLoginGradientLayer = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.loginHeroGradientDesktop};
  transform: rotate(180deg) scaleX(-1);
  transform-origin: center center;
`;

export const DesktopBrandPanel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 1;
  padding: 0 40px 0 clamp(24px, 4vw, 55px);
  box-sizing: border-box;
  pointer-events: none;

  & img {
    pointer-events: auto;
  }
`;

export const DesktopBrandLockup = styled.img`
  display: block;
  height: 93px;
  width: auto;
  max-width: min(228px, 42vw);
  flex-shrink: 0;
`;

export const LoginHeroLockupImg = styled.img<{ $deviceType: DeviceType }>`
  display: block;
  height: ${({ $deviceType }) => ($deviceType === "mobile" ? "28px" : "40px")};
  width: auto;
  max-width: 100%;
`;

export const LoginPrivacyPolicy = styled.div`
  display: flex;
  justify-content: center;
  margin: 24px;
  text-align: center;
  justify-self: center;
  max-width: 448px;

  p {
    font-size: 14px;
    line-height: 23px;
    color: ${({ theme }) => theme.neutral700};
    margin: 0;
  }

  a {
    color: inherit;
    text-decoration: underline;
    text-decoration-skip-ink: none;
  }
`;

export const LoginAccountWarning = styled.div`
  padding: 15px 20px;
  border-radius: 10px;
  background: var(--Primary-Green-Subtle, #eff8f5);
  color: #616366;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 23px;
  margin: 32px 12px 10px;
  display: inline-block;
  text-align: center;
  max-width: 448px;

  p {
    font-weight: 700;
    display: inline-block;
    margin: 0px;
  }
`;

export const StatusBannerWrapper = styled.div<{
  deviceType: DeviceType;
}>`
  width: ${({ deviceType }) => formFieldWidth(deviceType)};
  margin-bottom: ${({ deviceType }) =>
    deviceType === "mobile" ? "32px" : "56px"};
`;
